import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  Tune as TuneIcon,
  DragIndicator as DragIndicatorIcon,
  CloudUpload as CloudUploadIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/services/api';
import IndicatorDialog from './IndicatorDialog';
import WeightAdjustmentDialog from './WeightAdjustmentDialog';
import IndicatorSortDialog from './IndicatorSortDialog';
import IndicatorImportDialog from './IndicatorImportDialog';
import ProjectLogDialog from './ProjectLogDialog';
import AIIndicatorRecommendDialog from './AIIndicatorRecommendDialog';
import BulkOpinionSelectDialog from './BulkOpinionSelectDialog';
import SingleIndicatorOpinionDialog from './SingleIndicatorOpinionDialog';

// 指标数据类型定义
interface Indicator {
  id: number;
  level1_name: string;
  level2_name: string;
  indicator_name: string;
  indicator_code?: string;
  level1_code?: string;
  level2_code?: string;
  weight: number;
  score?: number;
  evaluation_rules?: string;
  evaluation_process_description?: string;
  evaluation_analysis_description?: string;
  evaluation_rule_type?: string;
  custom_fields_json?: string;
  display_order?: number;
  created_at: string;
  updated_at: string;
}

// JSON数据展示组件
const JsonCell: React.FC<{ data: any }> = ({ data }) => {
  let displayData = data;
  if (typeof data === 'object' && data !== null) {
    try {
      displayData = JSON.stringify(data, null, 2);
    } catch (e) {
      displayData = "Invalid JSON";
    }
  }
  return (
    <pre style={{ 
      whiteSpace: 'pre-wrap', 
      wordBreak: 'break-all', 
      margin: 0, 
      fontSize: '0.8rem' 
    }}>
      {String(displayData)}
    </pre>
  );
};

// 一级指标颜色样式
const getLevel1ColorStyles = (theme: any, level1Name: string) => {
  const styles: Record<string, any> = {
    '决策': {
      backgroundColor: theme.palette.mode === 'dark' ? '#1565c0' : '#e3f2fd',
      color: theme.palette.mode === 'dark' ? '#bbdefb' : '#0d47a1',
      borderRight: `3px solid ${theme.palette.mode === 'dark' ? '#1976d2' : '#90caf9'}`
    },
    '过程': {
      backgroundColor: theme.palette.mode === 'dark' ? '#ef6c00' : '#fff3e0',
      color: theme.palette.mode === 'dark' ? '#ffb74d' : '#e65100',
      borderRight: `3px solid ${theme.palette.mode === 'dark' ? '#f57c00' : '#ffcc02'}`
    },
    '产出': {
      backgroundColor: theme.palette.mode === 'dark' ? '#2e7d32' : '#e8f5e8',
      color: theme.palette.mode === 'dark' ? '#81c784' : '#1b5e20',
      borderRight: `3px solid ${theme.palette.mode === 'dark' ? '#388e3c' : '#66bb6a'}`
    },
    '效益': {
      backgroundColor: theme.palette.mode === 'dark' ? '#7b1fa2' : '#f3e5f5',
      color: theme.palette.mode === 'dark' ? '#ba68c8' : '#4a148c',
      borderRight: `3px solid ${theme.palette.mode === 'dark' ? '#8e24aa' : '#ab47bc'}`
    },
  };
  return { 
    textAlign: 'center', 
    fontWeight: 'bold', 
    ...(styles[level1Name] || {}) 
  };
};

const IndicatorManager: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const theme = useTheme();
  
  const [indicators, setIndicators] = useState<Indicator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // 对话框状态
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState<Indicator | null>(null);
  
  // 权重调整相关状态
  const [isWeightDialogOpen, setIsWeightDialogOpen] = useState(false);
  const [weightAdjustmentError, setWeightAdjustmentError] = useState('');
  
  // 排序相关状态
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [isSorting, setIsSorting] = useState(false);
  
  // 导入相关状态
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  
  // 项目日志管理状态
  const [projectLogDialogOpen, setProjectLogDialogOpen] = useState(false);
  const [hasLogAnalysis, setHasLogAnalysis] = useState(false);
  const [aiRecommendDialogOpen, setAiRecommendDialogOpen] = useState(false);
  
  // 批量删除相关状态
  const [selectedIndicatorIds, setSelectedIndicatorIds] = useState<number[]>([]);
  const [batchDeleteConfirmOpen, setBatchDeleteConfirmOpen] = useState(false);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  // 单个指标评价意见生成状态
  const [singleOpinionDialogOpen, setSingleOpinionDialogOpen] = useState(false);
  const [evaluatingIndicator, setEvaluatingIndicator] = useState<Indicator | null>(null);

  // 导出菜单状态
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  // 获取指标列表
  const fetchIndicators = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      setIndicators([]);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get(`/projects/${projectId}/indicators`);
      
      if (response.success && response.data) {
        // 与 deepwrite-v3 保持一致：仅按 indicator_code 升序（数值感知）排序
        const sortedData = (response.data || []).sort((a: Indicator, b: Indicator) => {
          if (!a.indicator_code) return 1;
          if (!b.indicator_code) return -1;
          return a.indicator_code.localeCompare(b.indicator_code, 'zh-Hans-CN', { numeric: true });
        });
        setIndicators(sortedData);
      } else {
        throw new Error(response.message || '获取指标列表失败');
      }
    } catch (err: any) {
      setError(err.message || '加载指标列表失败');
      setIndicators([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // 初始加载
  useEffect(() => {
    fetchIndicators();
  }, [fetchIndicators]);

  // 初始化检查一次日志分析状态
  useEffect(() => {
    if (projectId) {
      checkHasLatestLogAnalysis();
    }
  }, [projectId]);

  // 处理添加指标
  const handleAddIndicator = () => {
    setEditingIndicator(null);
    setDialogOpen(true);
  };

  // 处理编辑指标
  const handleEditIndicator = (indicator: Indicator) => {
    setEditingIndicator(indicator);
    setDialogOpen(true);
  };

  // 处理删除指标
  const handleDeleteIndicator = async (indicator: Indicator) => {
    if (!projectId) return;
    
    if (window.confirm(`确定要删除指标"${indicator.indicator_name}"吗？`)) {
      try {
        const response = await apiClient.delete(`/projects/${projectId}/indicators/${indicator.id}`);
        if (response.success) {
          // 重新加载指标列表
          await fetchIndicators();
        }
      } catch (error) {
      }
    }
  };

  // 处理保存指标
  const handleSaveIndicator = async (data: any, id?: number) => {
    if (!projectId) return;
    
    try {
      if (id) {
        // 编辑指标
        const response = await apiClient.put(`/projects/${projectId}/indicators/${id}`, data);
        if (response.success) {
          await fetchIndicators();
        }
      } else {
        // 创建新指标
        const response = await apiClient.post(`/projects/${projectId}/indicators`, data);
        if (response.success) {
          await fetchIndicators();
        }
      }
    } catch (error) {
      throw error;
    }
  };

  // 处理关闭对话框
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingIndicator(null);
  };

  // 权重调整对话框处理
  const handleOpenWeightDialog = () => {
    setIsWeightDialogOpen(true);
    setWeightAdjustmentError('');
  };

  const handleCloseWeightDialog = () => {
    setIsWeightDialogOpen(false);
    setWeightAdjustmentError('');
  };

  const handleSaveWeights = async (adjustedIndicators: Indicator[]) => {
    setWeightAdjustmentError('');
    try {
      const response = await apiClient.post(`/projects/${projectId}/indicators/batch-update-weights`, {
        indicators: adjustedIndicators.map(ind => ({ id: ind.id, weight: ind.weight }))
      });

      if (response.success) {
        await fetchIndicators(); // 刷新指标列表
        handleCloseWeightDialog();
      } else {
        throw new Error(response.message || '保存权重失败');
      }
    } catch (err: any) {
      setWeightAdjustmentError(err.message || '保存权重失败');
      throw err; // 重新抛出错误，让对话框知道保存失败
    }
  };

  // 排序相关处理函数
  const handleOpenSortDialog = () => {
    setSortDialogOpen(true);
  };

  const handleCloseSortDialog = () => {
    setSortDialogOpen(false);
  };

  const handleSaveSort = async (newCodes: any[]) => {
    setIsSorting(true);
    try {
      const response = await apiClient.post(`/projects/${projectId}/indicators/reorder`, {
        newCodes
      });

      if (response.success) {
        await fetchIndicators(); // 重新获取指标列表
        handleCloseSortDialog();
      } else {
        throw new Error(response.message || '排序保存失败');
      }
    } catch (err: any) {
      setError(err.message || '排序保存失败');
      throw err; // 重新抛出错误，让对话框知道保存失败
    } finally {
      setIsSorting(false);
    }
  };

  // 导入相关处理函数
  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
  };

  // 导出Excel
  const handleExportExcel = async () => {
    if (!projectId) return;
    setIsExporting(true);
    try {
      const resp = await fetch(`/api/v1/projects/${projectId}/indicators/export-excel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || '导出失败');
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `项目_${projectId}_指标清单.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message || '导出失败');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportWord = async () => {
    if (!projectId) return;
    setIsExporting(true);
    try {
      const resp = await fetch(`/api/v1/projects/${projectId}/indicators/export-word`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || '导出失败');
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `项目_${projectId}_指标清单.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message || '导出失败');
    } finally {
      setIsExporting(false);
    }
  };

  const openExportMenu = (e: React.MouseEvent<HTMLElement>) => setExportMenuAnchor(e.currentTarget);
  const closeExportMenu = () => setExportMenuAnchor(null);

  const handleImportSuccess = (_importedIndicators: any[]) => {
    // 刷新指标列表
    fetchIndicators();
    // 导入成功后自动触发一次后端排序（不传newCodes，走后端自动生成）
    (async () => {
      try {
        await apiClient.post(`/projects/${projectId}/indicators/reorder`, {});
        // 排序完成后刷新
        fetchIndicators();
      } catch (e) {
      }
    })();
  };

  // 批量删除相关函数
  const handleSelectIndicatorForBatchDelete = (indicatorId: number, selected: boolean) => {
    setSelectedIndicatorIds(prev => {
      if (selected) {
        return [...prev, indicatorId];
      } else {
        return prev.filter(id => id !== indicatorId);
      }
    });
  };

  const handleSelectAllIndicatorsForBatchDelete = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedIndicatorIds(indicators.map(ind => ind.id));
    } else {
      setSelectedIndicatorIds([]);
    }
  };

  const handleOpenBatchDeleteDialog = () => {
    if (selectedIndicatorIds.length === 0) {
      setError('请先选择要删除的指标');
      return;
    }
    setBatchDeleteConfirmOpen(true);
  };

  const handleCloseBatchDeleteDialog = () => {
    setBatchDeleteConfirmOpen(false);
  };

  const handleConfirmBatchDelete = async () => {
    if (selectedIndicatorIds.length === 0) return;
    
    setIsBatchDeleting(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/projects/${projectId}/indicators/batch-delete`, {
        indicator_ids: selectedIndicatorIds
      });

      if (response.success) {
        setSelectedIndicatorIds([]);
        fetchIndicators(); // 刷新列表
        setBatchDeleteConfirmOpen(false);
      } else {
        throw new Error(response.error || '批量删除失败');
      }
    } catch (err: any) {
      setError(err.message || '批量删除失败');
    } finally {
      setIsBatchDeleting(false);
    }
  };

  // 处理评价指标
  const handleEvaluateIndicator = (indicator: Indicator) => {
    setEvaluatingIndicator(indicator);
    setSingleOpinionDialogOpen(true);
  };

  // 检查是否已有日志分析（只查询 writing 类型）
  const checkHasLatestLogAnalysis = useCallback(async () => {
    if (!projectId) return;
    try {
      // 查询所有 writing 类型的日志
      const response = await apiClient.get(`/projects/${projectId}/facts/?log_type=writing`);
      if (response.success && response.data) {
        const logs = response.data.logs || [];
        setHasLogAnalysis(logs.length > 0);
      } else {
        setHasLogAnalysis(false);
      }
    } catch (e) {
      setHasLogAnalysis(false);
    }
  }, [projectId]);

  // 项目日志管理相关处理函数
  const handleOpenProjectLogDialog = () => {
    setProjectLogDialogOpen(true);
  };

  const handleCloseProjectLogDialog = () => {
    setProjectLogDialogOpen(false);
    // 关闭后检查是否已有日志（用于显示"一键生成评价意见"按钮）
    checkHasLatestLogAnalysis();
  };

  // AI指标推荐相关处理函数
  const handleOpenAiRecommendDialog = () => {
    setAiRecommendDialogOpen(true);
  };

  const handleCloseAiRecommendDialog = () => {
    setAiRecommendDialogOpen(false);
  };

  const handleAiRecommendationComplete = async (result: any) => {
    try {
      if (result.success && result.data && result.data.recommendedIndicators) {
        const recommendedIndicators = result.data.recommendedIndicators;
        
        // 直接创建指标，不显示确认对话框
        // 批量创建指标
        await batchCreateIndicators(recommendedIndicators);
        setSuccess('AI指标推荐成功！指标已添加到项目中。');
        await fetchIndicators(); // 刷新指标列表
      } else {
        setError('AI推荐失败：' + (result.error || '未知错误'));
      }
    } catch (error: any) {
      setError('处理AI推荐结果失败：' + (error.message || '未知错误'));
    }
  };

  // 一键生成评价意见（基于日志关联片段）
  const [isBulkGeneratingOpinions, setIsBulkGeneratingOpinions] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  const handleBulkGenerateOpinionsFromLogs = async (selectedIndicatorIds?: number[]) => {
    if (!projectId) return;
    
    setIsBulkGeneratingOpinions(true);
    setError(null);
    
    try {
      
      // 确定要处理的指标列表
      const indicatorsToProcess = selectedIndicatorIds && selectedIndicatorIds.length > 0
        ? indicators.filter(ind => selectedIndicatorIds.includes(ind.id) && ind.evaluation_rules)
        : indicators.filter(ind => ind.evaluation_rules);
      
      
      let successCount = 0;
      
      // 逐个处理指标（deepwrite-v3的逻辑：前端循环调用后端API）
      for (const indicator of indicatorsToProcess) {
        try {
          
          // 步骤1：预览模式 - 获取事实内容
          const previewResponse = await apiClient.post(
            `/projects/${projectId}/indicators/${indicator.id}/generate-opinion`,
            { save_to_database: false },
            { timeout: 300000 }
          );
          
          if (!previewResponse.success || !previewResponse.data) {
            throw new Error('获取事实内容失败');
          }
          
          const facts = previewResponse.data.facts;
          if (!facts || previewResponse.data.no_relevant_content) {
            continue;
          }
          
          // 步骤2：保存模式 - 使用获取的事实生成评价意见
          const saveResponse = await apiClient.post(
            `/projects/${projectId}/indicators/${indicator.id}/generate-opinion`,
            { 
              save_to_database: true,
              edited_facts: facts
            },
            { timeout: 300000 }
          );
          
          if (saveResponse.success) {
            successCount++;
            
            // 立即刷新指标列表，显示最新结果（deepwrite-v3的关键）
            await fetchIndicators();
          }
        } catch (err: any) {
          // 单个失败继续处理下一个
        }
      }
      
      // 显示完成消息
      setSuccess(`已完成 ${successCount}/${indicatorsToProcess.length} 个指标的评价意见生成`);
      setTimeout(() => setSuccess(null), 4000);
      
    } catch (err: any) {
      setError(err.message || '一键生成评价意见失败');
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsBulkGeneratingOpinions(false);
      setBulkDialogOpen(false);
    }
  };

  const openBulkOpinionDialog = () => {
    setBulkDialogOpen(true);
  };

  // 批量创建指标
  const batchCreateIndicators = async (indicators: any[]) => {
    try {
      
      // 转换指标数据格式，确保符合后端 IndicatorCreate 模型
      const formattedIndicators = indicators.map((indicator, index) => ({
        indicator_name: indicator.indicator_name,
        level1_name: indicator.level1_name,
        level2_name: indicator.level2_name,
        evaluation_rule_type: indicator.evaluation_rule_type || '要素符合度计分',
        evaluation_rules: indicator.evaluation_rules || '',
        weight: indicator.weight || 1.0,
        target_value: indicator.target_value || '',
        display_order: (index + 1) * 10 // 设置显示顺序
      }));
      
      
      // 使用批量创建接口
      const response = await apiClient.post(`/projects/${projectId}/indicators/batch`, formattedIndicators);
      
      
      if (response.success) {
        
        // 创建成功后，自动触发重新排序（生成indicator_code）- 与deepwrite-v3保持一致
        try {
          const reorderResponse = await apiClient.post(`/projects/${projectId}/indicators/reorder`, {});
          if (reorderResponse.success) {
          } else {
          }
        } catch (reorderError: any) {
          // 排序失败不影响主流程，只记录警告
        }
        
      } else {
        throw new Error(response.message || '批量创建失败');
      }
    } catch (error: any) {
      throw new Error('批量创建指标失败：' + (error.response?.data?.detail || error.message));
    }
  };

  if (!projectId) {
    return <Alert severity="warning">请先选择一个项目来管理指标。</Alert>;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">项目指标管理</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddIndicator}
            sx={{
              color: theme.palette.mode === 'dark' ? '#42a5f5' : undefined,
              borderColor: theme.palette.mode === 'dark' ? '#42a5f5' : undefined,
              '&:hover': {
                borderColor: theme.palette.mode === 'dark' ? '#1976d2' : undefined,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(66, 165, 245, 0.08)' : undefined
              }
            }}
          >
            添加新指标
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={handleOpenImportDialog}
            sx={{
              color: theme.palette.mode === 'dark' ? '#42a5f5' : undefined,
              borderColor: theme.palette.mode === 'dark' ? '#42a5f5' : undefined,
              '&:hover': {
                borderColor: theme.palette.mode === 'dark' ? '#1976d2' : undefined,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(66, 165, 245, 0.08)' : undefined
              }
            }}
          >
            导入指标
          </Button>


          {/* AI指标推荐按钮 - 只在没有指标时显示 */}
          {indicators.length === 0 && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleOpenAiRecommendDialog}
              sx={{
                color: theme.palette.mode === 'dark' ? '#ab47bc' : undefined,
                borderColor: theme.palette.mode === 'dark' ? '#ab47bc' : undefined,
                '&:hover': {
                  borderColor: theme.palette.mode === 'dark' ? '#8e24aa' : undefined,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(171, 71, 188, 0.08)' : undefined
                }
              }}
            >
              AI指标推荐
            </Button>
          )}
          
          {selectedIndicatorIds.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleOpenBatchDeleteDialog}
              sx={{
                color: theme.palette.mode === 'dark' ? '#f44336' : undefined,
                borderColor: theme.palette.mode === 'dark' ? '#f44336' : undefined,
                '&:hover': {
                  borderColor: theme.palette.mode === 'dark' ? '#d32f2f' : undefined,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.08)' : undefined
                }
              }}
            >
              批量删除 ({selectedIndicatorIds.length})
            </Button>
          )}
          
          {indicators.length > 0 && (
            <>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DragIndicatorIcon />}
                onClick={handleOpenSortDialog}
                disabled={isSorting}
                sx={{
                  color: theme.palette.mode === 'dark' ? '#42a5f5' : undefined,
                  borderColor: theme.palette.mode === 'dark' ? '#42a5f5' : undefined,
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? '#1976d2' : undefined,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(66, 165, 245, 0.08)' : undefined
                  }
                }}
              >
                {isSorting ? '排序中...' : '指标排序'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<TuneIcon />}
                onClick={handleOpenWeightDialog}
                sx={{
                  color: theme.palette.mode === 'dark' ? '#42a5f5' : undefined,
                  borderColor: theme.palette.mode === 'dark' ? '#42a5f5' : undefined,
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? '#1976d2' : undefined,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(66, 165, 245, 0.08)' : undefined
                  }
                }}
              >
                权重调整
              </Button>
              
              {/* 上传项目日志按钮 */}
              {/* <Button
                variant="outlined"
                color="info"
                startIcon={<CloudUploadIcon />}
                onClick={handleOpenProjectLogDialog}
                sx={{
                  color: theme.palette.mode === 'dark' ? '#81c784' : undefined,
                  borderColor: theme.palette.mode === 'dark' ? '#81c784' : undefined,
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? '#66bb6a' : undefined,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(129, 199, 132, 0.08)' : undefined
                  }
                }}
              >
                上传项目日志
              </Button> */}
              
              {/* 一键生成评价意见按钮 - 只有当有日志分析时才显示 */}
              {hasLogAnalysis && (
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() => openBulkOpinionDialog()}
                  disabled={isBulkGeneratingOpinions}
                  sx={{
                    color: theme.palette.mode === 'dark' ? '#81c784' : undefined,
                    borderColor: theme.palette.mode === 'dark' ? '#81c784' : undefined,
                    '&:hover': {
                      borderColor: theme.palette.mode === 'dark' ? '#66bb6a' : undefined,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(129, 199, 132, 0.08)' : undefined
                    }
                  }}
                >
                  一键生成评价意见 {isBulkGeneratingOpinions && <CircularProgress size={16} sx={{ ml: 1 }} />}
                </Button>
              )}
              
              {/* 导出按钮 - 放在最后 */}
              <Button
                variant="outlined"
                color="success"
                startIcon={<GetAppIcon />}
                onClick={openExportMenu}
                disabled={isExporting}
                sx={{
                  color: theme.palette.mode === 'dark' ? '#66bb6a' : undefined,
                  borderColor: theme.palette.mode === 'dark' ? '#66bb6a' : undefined,
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? '#4caf50' : undefined,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(102, 187, 106, 0.08)' : undefined
                  }
                }}
              >
                {isExporting ? '导出中...' : '导出'}
              </Button>
              <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)} onClose={closeExportMenu}>
                <MenuItem onClick={() => { closeExportMenu(); handleExportExcel(); }} disabled={isExporting}>
                  导出为 Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={() => { closeExportMenu(); handleExportWord(); }} disabled={isExporting}>
                  导出为 Word (.docx)
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {indicators.length === 0 && !loading && !error && (
        <Alert severity="info">该项目当前没有指标。请添加一个新指标。</Alert>
      )}

      {indicators.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'action.hover' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '3%' }}>
                  <Checkbox
                    checked={selectedIndicatorIds.length === indicators.length && indicators.length > 0}
                    indeterminate={selectedIndicatorIds.length > 0 && selectedIndicatorIds.length < indicators.length}
                    onChange={(e) => handleSelectAllIndicatorsForBatchDelete(e.target.checked)}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '2%' }}>序号</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '4%' }}>一级指标</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '4%' }}>二级指标</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>三级指标</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '3%' }}>权重</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '3%' }}>得分</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>评价规则</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>评价意见</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '6%', textAlign: 'right' }}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {indicators.map((indicator, index) => {
                const l1Style = getLevel1ColorStyles(theme, indicator.level1_name);

                return (
                  <TableRow key={indicator.id} hover>
                    <TableCell>
                      <Checkbox
                        checked={selectedIndicatorIds.includes(indicator.id)}
                        onChange={(e) => handleSelectIndicatorForBatchDelete(indicator.id, e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell sx={l1Style}>
                      {(indicator.level1_code && indicator.level1_code !== 'NO') ? indicator.level1_code : ''}{indicator.level1_name}
                    </TableCell>
                    <TableCell>
                      {(indicator.level2_code && indicator.level2_code !== 'NO') ? indicator.level2_code : ''}{indicator.level2_name}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <Box>
                        <Typography variant="body2">
                          {(indicator.indicator_code && !indicator.indicator_code.startsWith('NO')) ? indicator.indicator_code : ''}{indicator.indicator_name}
                        </Typography>
                        <Chip 
                          label={indicator.evaluation_rule_type || '要素符合度计分'} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{indicator.weight}</TableCell>
                    <TableCell>{
                      typeof indicator.score === 'number'
                        ? indicator.score.toFixed(2)
                        : (indicator.score ? Number(indicator.score).toFixed(2) : '')
                    }</TableCell>
                    <TableCell><JsonCell data={indicator.evaluation_rules} /></TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Box flex={1} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <JsonCell data={indicator.evaluation_process_description || ""} />
                        </Box>
                        {indicator.evaluation_analysis_description && (
                          <Tooltip 
                            title={indicator.evaluation_analysis_description} 
                            placement="top-start"
                            sx={{ 
                              '& .MuiTooltip-tooltip': { 
                                maxWidth: 500, 
                                fontSize: '0.875rem',
                                whiteSpace: 'pre-wrap' 
                              } 
                            }}
                          >
                            <span style={{ marginLeft: 6, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                              <span role="img" aria-label="分析事实">💡</span>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="评价指标">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEvaluateIndicator(indicator)} 
                          color="info" 
                          aria-label="评价指标"
                          disabled={!indicator.evaluation_rules}
                        >
                          <AutoAwesomeIcon fontSize="small"/>
                        </IconButton>
                      </Tooltip>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditIndicator(indicator)} 
                        color="primary" 
                        aria-label="编辑"
                      >
                        <EditIcon fontSize="small"/>
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteIndicator(indicator)} 
                        color="error" 
                        aria-label="删除"
                      >
                        <DeleteIcon fontSize="small"/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* 指标对话框 */}
      <IndicatorDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        indicator={editingIndicator}
        onSave={handleSaveIndicator}
        projectId={projectId || ''}
        existingIndicators={indicators}
      />

      <WeightAdjustmentDialog
        open={isWeightDialogOpen}
        onClose={handleCloseWeightDialog}
        indicators={indicators}
        onSave={handleSaveWeights}
        error={weightAdjustmentError}
        clearError={() => setWeightAdjustmentError('')}
      />

      <IndicatorSortDialog
        open={sortDialogOpen}
        onClose={handleCloseSortDialog}
        indicators={indicators}
        onSave={handleSaveSort}
        isSaving={isSorting}
      />

      <IndicatorImportDialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        projectId={projectId || ''}
        onImportSuccess={handleImportSuccess}
      />

      {/* 项目日志管理对话框 */}
      <ProjectLogDialog
        open={projectLogDialogOpen}
        onClose={handleCloseProjectLogDialog}
        projectId={Number(projectId)}
      />

      {/* AI指标推荐对话框 */}
      <AIIndicatorRecommendDialog
        open={aiRecommendDialogOpen}
        onClose={handleCloseAiRecommendDialog}
        project={{
          id: projectId || '',
          project_name: '',
          industry_type: '',
          client_level: '',
          evaluation_type: '',
          description: ''
        }}
        onRecommendationComplete={handleAiRecommendationComplete}
      />

      {/* 批量删除确认对话框 */}
      <Dialog
        open={batchDeleteConfirmOpen}
        onClose={handleCloseBatchDeleteDialog}
        aria-labelledby="batch-delete-dialog-title"
        aria-describedby="batch-delete-dialog-description"
      >
        <DialogTitle id="batch-delete-dialog-title">
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">确认批量删除指标</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="batch-delete-dialog-description">
            <Typography variant="body1" gutterBottom>
              此操作不可恢复，确定要删除选中的 {selectedIndicatorIds.length} 个指标吗？
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              选中的指标：
            </Typography>
            <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
              {indicators
                .filter(ind => selectedIndicatorIds.includes(ind.id))
                .map((indicator, index) => (
                  <Typography key={indicator.id} variant="body2" sx={{ py: 0.5 }}>
                    {index + 1}. {indicator.indicator_name}
                  </Typography>
                ))}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBatchDeleteDialog} disabled={isBatchDeleting}>
            取消
          </Button>
          <Button 
            onClick={handleConfirmBatchDelete} 
            color="error" 
            variant="contained" 
            disabled={isBatchDeleting}
            startIcon={isBatchDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isBatchDeleting ? '删除中...' : `确认删除 (${selectedIndicatorIds.length}个)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 选择要生成的指标 */}
      <BulkOpinionSelectDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        indicators={indicators}
        loading={isBulkGeneratingOpinions}
        onConfirm={(ids) => handleBulkGenerateOpinionsFromLogs(ids)}
      />

      {/* 单个指标评价意见生成对话框 */}
      <SingleIndicatorOpinionDialog
        open={singleOpinionDialogOpen}
        onClose={() => {
          setSingleOpinionDialogOpen(false);
          setEvaluatingIndicator(null);
        }}
        indicator={evaluatingIndicator}
        projectId={Number(projectId)}
        onSuccess={() => {
          fetchIndicators();
        }}
      />
    </Paper>
  );
};

export default IndicatorManager;
